import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { MRT_ColumnDef, useMaterialReactTable } from 'material-react-table';
import { Box, IconButton, Slide, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from 'src/presentations/components/atom/button';
import { SettingsBaseTable } from 'src/presentations/components/molecules/settings/table/BaseTable';
import { createTableConfig } from 'src/presentations/utils/tableConfigUtils';
import { SettingsMain } from '../organisms/settings/SettingsMain';

export interface SettingsTableLayoutProps<TEntity extends { id?: number | string }> {
  // Content props
  title: string;
  subtitle: string;
  backPath: string;

  // Table props
  columns: MRT_ColumnDef<TEntity>[];
  data: TEntity[];
  totalItems: number;
  isLoading: boolean;
  isError?: boolean;

  // Table state
  pagination: any;
  sorting: any;
  onChangePage: (page: number) => void;
  onChangePageSize: (size: number) => void;
  onSortingChange: (sorting: any) => void;

  // Table operations
  onSaveEntity: (params: any) => Promise<void>;
  onCancelEdit: () => void;

  // Detail panel
  selectedEntityId: number | string | null;
  onCloseDetail: () => void;
  renderDetail: (entityId: number | string) => ReactNode;

  // Optional customizations
  rowCount?: number;
  customRowProps?: (props: any) => any;
  customCellProps?: (props: any) => any;
  enableRowActions?: boolean;
  onDeleteRow?: (row: any) => void;
  additionalTopActions?: ReactNode;
  detailPanelTop?: number;

  // Row selection
  enableRowSelection?: boolean;
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: (updaterOrValue: Record<string, boolean> | ((old: Record<string, boolean>) => Record<string, boolean>)) => void;
}

const firstCellStyles = {
  boxShadow: '4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
  borderLeft: '1px solid #E5E7EB',
  width: '70px',
  padding: '0 10px 0 10px !important',

  '& .MuiBox-root': {
    gap: '0!important',
    width: '70px',
    justifyContent: 'center',

    '& svg': {
      width: '20px!important',
    },
  },
};

export function SettingsTableLayout<TEntity extends { id?: number | string }>({
  title,
  subtitle,
  backPath,
  columns,
  data,
  totalItems,
  isLoading,
  isError = false,
  pagination,
  sorting,
  onChangePage,
  onChangePageSize,
  onSortingChange,
  onSaveEntity,
  onCancelEdit,
  selectedEntityId,
  onCloseDetail,
  renderDetail,
  rowCount,
  customRowProps,
  customCellProps,
  enableRowActions = true,
  onDeleteRow,
  additionalTopActions,
  detailPanelTop = 0,
  enableRowSelection = false,
  rowSelection = {},
  onRowSelectionChange,
}: SettingsTableLayoutProps<TEntity>) {
  // Keep the last non-null entityId so the detail panel content stays mounted during the slide-out animation
  const [displayedEntityId, setDisplayedEntityId] = useState<number | string | null>(selectedEntityId);
  useEffect(() => {
    if (selectedEntityId !== null && selectedEntityId !== undefined) {
      setDisplayedEntityId(selectedEntityId);
    }
  }, [selectedEntityId]);

  // Render row actions
  const renderRowActions = useCallback(
    ({ row, table }) => (
      <Box sx={{ display: 'flex' }}>
        <Tooltip title='Edit'>
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        {onDeleteRow && (
          <Tooltip title='Delete'>
            <IconButton color='error' onClick={() => onDeleteRow(row)}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
    [onDeleteRow]
  );

  // Base table configuration
  const baseTableConfig = createTableConfig<TEntity>({
    onCreatingCancel: onCancelEdit,
    onEditingCancel: onCancelEdit,
    enableRowSelection,
  });

  // Wrap the async save function to handle Promise properly
  const handleSaveEntity = useCallback(
    (params: any) => {
      // Call the async function but don't return the Promise
      onSaveEntity(params).catch((error) => {
        // Error handling is already done in the onSaveEntity function
        console.error('Entity save error:', error);
      });
    },
    [onSaveEntity]
  );

  // Create table instance
  const table = useMaterialReactTable({
    ...baseTableConfig,
    columns,
    data,
    state: {
      isLoading,
      showAlertBanner: isError,
      sorting,
      rowSelection,
    },
    onRowSelectionChange,
    manualPagination: true,
    rowCount: rowCount || totalItems,
    manualSorting: true,
    muiToolbarAlertBannerProps: {
      color: 'error',
      children: 'Error loading data',
    },
    enableEditing: true,
    muiSelectCheckboxProps: {
      sx: {
        color: '#00A651',
        '&.Mui-checked': {
          color: '#00A651',
        },
      },
    },
    onEditingRowSave: handleSaveEntity,
    onCreatingRowSave: handleSaveEntity,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
      },
    },
    muiTableHeadProps: {
      sx: {
        '& th:first-child': firstCellStyles,
      },
    },
    muiTableBodyProps: {
      sx: {
        '& td:first-child': firstCellStyles,
      },
    },
    muiTableBodyRowProps:
      customRowProps ||
      (({ row }) => ({
        sx: {
          backgroundColor: selectedEntityId === row.original.id ? 'rgb(242, 242, 242)' : 'inherit',
          '&:hover': {
            backgroundColor: 'rgb(242, 242, 242)',
          },
        },
      })),
    muiTableBodyCellProps:
      customCellProps ||
      (({ row }) => ({
        onClick: () => {
          // This will be overridden by the specific implementation
        },
        sx: {
          backgroundColor: selectedEntityId === row.original.id ? 'rgb(242, 242, 242)' : 'inherit',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgb(242, 242, 242)',
            textDecoration: 'underline',
          },
        },
      })),
    onSortingChange: (updatedSorting) => {
      if (typeof updatedSorting === 'function') {
        const newSortingState = updatedSorting(sorting);
        onSortingChange(newSortingState);
      } else {
        onSortingChange(updatedSorting);
      }
    },
    renderRowActions: enableRowActions ? renderRowActions : undefined,
  });

  const topActions = useMemo(
    () => (
      <>
        <Button
          variant='primary'
          className='!pr-12 !pl-16 !ml-12'
          onClick={() => {
            table.setCreatingRow(true);
          }}
        >
          <div className='flex items-center gap-10'>
            <span className='text-14 xl:text-16'>Add New</span>
            <AddIcon />
          </div>
        </Button>
        {additionalTopActions}
      </>
    ),
    [table, additionalTopActions]
  );

  return (
    <SettingsMain title={title} subtitle={subtitle} backPath={backPath} topActions={topActions}>
      <div className='h-full w-full flex flex-col relative'>
        <SettingsBaseTable table={table} totalItems={totalItems} pagination={pagination} onChangePage={onChangePage} onChangePageSize={onChangePageSize} />
      </div>

      <Slide direction='left' in={!!selectedEntityId} mountOnEnter unmountOnExit>
        <div style={{ position: 'absolute', top: detailPanelTop, right: 0, bottom: 0, width: '50%', zIndex: 10 }}>
          {displayedEntityId !== null && renderDetail(displayedEntityId)}
        </div>
      </Slide>
    </SettingsMain>
  );
}
